import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../../utils/apiUtils';

// Create Context
export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Check if user is stored in localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                localStorage.removeItem('user');
            }
        }
        setLoading(false);
    }, []);

    const login = async (email, password) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(buildApiUrl('login'), {
                email,
                password
            });

            if (response.data.success) {
                const userData = response.data.user;
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
                return { success: true };
            } else {
                setError(response.data.message);
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'An error occurred during login';
            setError(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    const register = async (username, email, password, first_name, last_name) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(buildApiUrl('register'), {
                username,
                email,
                password,
                first_name,
                last_name
            });

            if (response.data.success) {
                return { success: true };
            } else {
                setError(response.data.message);
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'An error occurred during registration';
            setError(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('user');
    };

    const updateProfile = async (username, email, first_name, last_name) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.put(
                buildApiUrl('profile'),
                { username, email, first_name, last_name },
                { headers: { 'X-User-ID': user.id } }
            );

            if (response.data.success) {
                const updatedUser = {
                    ...user,
                    username,
                    email,
                    first_name,
                    last_name
                };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                return { success: true };
            } else {
                setError(response.data.message);
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'An error occurred while updating profile';
            setError(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    const updatePassword = async (currentPassword, newPassword) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.put(
                buildApiUrl('password'),
                { current_password: currentPassword, new_password: newPassword },
                { headers: { 'X-User-ID': user.id } }
            );

            if (response.data.success) {
                return { success: true };
            } else {
                setError(response.data.message);
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'An error occurred while updating password';
            setError(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    const getUserOrders = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(
                buildApiUrl('orders'),
                { headers: { 'X-User-ID': user.id } }
            );

            if (response.data.success) {
                return { success: true, orders: response.data.orders };
            } else {
                setError(response.data.message);
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'An error occurred while fetching orders';
            setError(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    const getUserProducts = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(
                buildApiUrl('products'),
                { headers: { 'X-User-ID': user.id } }
            );

            if (response.data.success) {
                return { success: true, products: response.data.products };
            } else {
                setError(response.data.message);
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'An error occurred while fetching products';
            setError(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    // Add auth header to axios requests
    axios.interceptors.request.use(
        (config) => {
            if (user) {
                config.headers['X-User-ID'] = user.id;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    const contextValue = {
        user,
        loading,
        error,
        login,
        register,
        logout,
        updateProfile,
        updatePassword,
        getUserOrders,
        getUserProducts,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider; 